import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Package, MapPin, User } from "lucide-react";
import { accountApi, useCustomerAuth } from "@/context/CustomerAuthContext";

const field = "w-full bg-cream border border-forest/15 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-gold";
const statusCls = { placed: "bg-[#FBF6E8] text-gold", confirmed: "bg-[#E8F0EA] text-forest", shipped: "bg-blue-50 text-blue-700", delivered: "bg-[#E8F0EA] text-forest", cancelled: "bg-red-50 text-red-700" };

export default function Account() {
  const { user, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({ name: user.name || "", phone: user.phone || "", address: user.address || "", city: user.city || "", state: user.state || "", pincode: user.pincode || "" });
    accountApi.get("/account/orders").then(({ data }) => setOrders(data)).catch(() => {});
  }, [user]);

  if (user === null) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-forest" /></div>;
  if (!user) return <Navigate to="/account/login" replace />;
  if (!form) return null;

  const on = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await accountApi.put("/account/profile", form); toast.success("Details saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const signOut = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="account-page" className="container-nb py-12 sm:py-20">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div className="flex items-center gap-4">
          {user.picture ? <img src={user.picture} alt="" className="w-14 h-14 rounded-full object-cover" /> : <span className="w-14 h-14 rounded-full bg-forest/10 text-forest font-serif text-xl flex items-center justify-center">{(user.name || user.email)[0].toUpperCase()}</span>}
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl text-forest">Hello, {user.name?.split(" ")[0] || "there"}</h1>
            <p className="text-sm text-mutedgreen">{user.email}</p>
          </div>
        </div>
        <button data-testid="account-logout-button" onClick={signOut} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-forest border border-forest/20 px-5 py-2.5 rounded-full hover:bg-forest/5"><LogOut size={14} /> Sign out</button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <form data-testid="account-profile-form" onSubmit={save} className="bg-white border border-forest/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-5"><MapPin size={16} className="text-gold" /><h2 className="text-xs font-bold uppercase tracking-[0.16em] text-forest">Saved delivery address</h2></div>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="profile-name-input" value={form.name} onChange={on("name")} placeholder="Full name" className={field} />
              <input data-testid="profile-phone-input" value={form.phone} onChange={on("phone")} placeholder="Phone" className={field} />
            </div>
            <input data-testid="profile-address-input" value={form.address} onChange={on("address")} placeholder="Address" className={field} />
            <div className="grid grid-cols-3 gap-4">
              <input data-testid="profile-city-input" value={form.city} onChange={on("city")} placeholder="City" className={field} />
              <input data-testid="profile-state-input" value={form.state} onChange={on("state")} placeholder="State" className={field} />
              <input data-testid="profile-pincode-input" value={form.pincode} onChange={on("pincode")} placeholder="Pincode" className={field} />
            </div>
            <button type="submit" data-testid="profile-save-button" disabled={busy} className="inline-flex items-center gap-2 bg-forest text-cream text-xs font-bold uppercase tracking-[0.14em] px-6 py-2.5 rounded-full hover:bg-gold transition-colors disabled:opacity-60">
              {busy && <Loader2 size={13} className="animate-spin" />} Save details
            </button>
          </div>
        </form>

        <div data-testid="account-orders" className="bg-white border border-forest/10 rounded-2xl p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-5"><Package size={16} className="text-gold" /><h2 className="text-xs font-bold uppercase tracking-[0.16em] text-forest">Order history</h2></div>
          {orders.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-sm text-mutedgreen">No orders yet.</p>
              <Link to="/collection" className="inline-block mt-3 text-xs font-bold uppercase tracking-[0.14em] text-forest link-underline">Browse the collection</Link>
            </div>
          ) : (
            <ul className="space-y-4">
              {orders.map((o) => (
                <li key={o.order_number} data-testid={`account-order-${o.order_number}`} className="border border-forest/10 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3">
                    <Link to={`/order/${o.order_number}`} className="text-sm font-semibold text-forest hover:text-gold">{o.order_number}</Link>
                    <span className={`text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${statusCls[o.status] || statusCls.placed}`}>{o.status}</span>
                  </div>
                  <p className="text-xs text-mutedgreen mt-1.5">{o.items.length} item{o.items.length > 1 ? "s" : ""} · ₹{o.total} · {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
